import { useState, useCallback } from "react";
import { message } from "@/utils/toast";
import { notificationApi } from "../api/notification";
import { useSSE } from "./useSSE";
import { notify } from "../components/Notification/notify";
import { KUN_SSE_ACTIVITY_EVENT, KunSseActivity } from "../components/genie/kunState";
import { activityFromSsePayload, isSseSignal } from "../utils/station/sseActivity";
import type {
  Notification,
  NotificationDetail,
  NotificationSearchParams,
  PageMetadata,
} from "../types/notification";

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState<PageMetadata | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Helper to safely get/set typed globals on window without using `any` all over
  type WinGlobals = {
    __lastShownNotificationId?: number;
    __lastShownUnread?: number;
    __exportExpected?: number;
    __exportObserved?: number;
    __exportPollIntervalId?: number | null;
    __exportPollTimeoutId?: number | null;
    __exportPollRef?: ((ev: Event) => void) | null;
    [key: string]: unknown;
  };
  const getWin = (): WinGlobals => window as unknown as WinGlobals;

  // Handle new notifications from SSE
  const handleNewNotification = useCallback(async (notification: Notification) => {
    const wg = getWin();
    const lastId = wg.__lastShownNotificationId;
    const shouldShow = lastId !== notification.id;

    setNotifications((prev) => [notification, ...prev]);

    if (shouldShow) {
      void activityFromSsePayload(notification).then((activity) => {
        if (!activity) return;
        window.dispatchEvent(
          new CustomEvent<KunSseActivity>(KUN_SSE_ACTIVITY_EVENT, { detail: activity })
        );
      });
    }

    // Signals (a new email, a conversation status change) are spoken by the
    // mascot and do not count as unread notifications.
    if (isSseSignal(notification)) {
      wg.__lastShownNotificationId = notification.id;
      return;
    }

    if (shouldShow) {
      try {
        // Get notification detail to include more content and download link
        const detail = await getNotificationDetail(notification.id);
        let message = notification.title;
        let actions: any;

        if (detail) {
          message = detail.text || notification.title;
          const attachmentId = detail.parameters?.ATTACHMENT_ID as string;
          if (attachmentId) {
            actions = [
              {
                attachmentId: attachmentId,
              },
            ];
          }
        }

        notify.success(message, {
          icon: "check-circle",
          title: "Export Done",
          actions,
        });
        wg.__lastShownNotificationId = notification.id;
      } catch (e) {
        console.error("Error showing notification:", e);
      }
    }

    // Update unread count and, if we decide to show the toast for this
    // notification, also update the global trackers so polling UI doesn't
    // duplicate the popup.
    setUnreadCount((prev) => {
      const newCount = prev + 1;
      if (shouldShow) {
        try {
          wg.__lastShownUnread = newCount;

          // increment observed export notifications and clear poll if we've reached the expected count
          try {
            wg.__exportObserved = (wg.__exportObserved || 0) + 1;
            if (wg.__exportExpected && wg.__exportObserved >= wg.__exportExpected) {
              // reset counters and clear poll
              wg.__exportExpected = 0;
              wg.__exportObserved = 0;
              if (wg.__exportPollIntervalId) {
                clearInterval(wg.__exportPollIntervalId as number);
                wg.__exportPollIntervalId = null;
              }
              if (wg.__exportPollTimeoutId) {
                clearTimeout(wg.__exportPollTimeoutId as number);
                wg.__exportPollTimeoutId = null;
              }
              // clear arrays too if present
              if (Array.isArray(wg.__exportPollIntervalIds)) {
                wg.__exportPollIntervalIds.forEach((id) => {
                  try {
                    clearInterval(id);
                  } catch (e) {
                    // ignore
                  }
                });
                wg.__exportPollIntervalIds = [];
              }
              if (Array.isArray(wg.__exportPollTimeoutIds)) {
                wg.__exportPollTimeoutIds.forEach((id) => {
                  try {
                    clearTimeout(id);
                  } catch (e) {
                    // ignore
                  }
                });
                wg.__exportPollTimeoutIds = [];
              }
              if (wg.__exportPollRef) {
                window.removeEventListener(
                  "notification:refreshed",
                  wg.__exportPollRef as EventListener
                );
                wg.__exportPollRef = null;
              }
            }
          } catch (e) {
            // ignore
          }
        } catch (e) {
          // ignore — don't block updating unread
        }
      }
      return newCount;
    });
  }, []);

  // Initialize SSE connection
  const { connect, disconnect, isConnected } = useSSE({
    onNotification: handleNewNotification,
    onError: () => {
      // message.error('Connection to notification service lost')
      setTimeout(() => connect(), 3000);
    },
    onConnect: () => {
      console.log("Connected to notification service");
    },
  });
  if (typeof window !== "undefined") {
    window.reconnectNotificationSSE = connect;
  }

  // Search notifications
  const searchNotifications = useCallback(
    async (params: NotificationSearchParams): Promise<number> => {
      setLoading(true);
      try {
        const response = await notificationApi.search(params);
        const content = response?._embedded?.sseSearchResponses || [];
        const metadata = response?.page || {
          size: 10,
          number: 0,
          totalPages: 0,
          totalElements: 0,
        };

        setNotifications(content);
        setMetadata(metadata);

        // If searching for unread notifications specifically, use the total count
        if (params.isRead === false) {
          const totalUnread = metadata.totalElements || 0;
          setUnreadCount(totalUnread);
          console.log("Updated unread count to:", totalUnread);
        } else {
          // For general searches, also update unread count by making a separate query
          try {
            const unreadResponse = await notificationApi.search({
              page: 0,
              size: 1,
              isRead: false,
              sort: "id desc",
            });
            const totalUnread = unreadResponse?.page?.totalElements || 0;
            setUnreadCount(totalUnread);
          } catch (unreadError) {
            console.error("Error fetching unread count:", unreadError);
            // Fallback to current page count if total count fails
            const unreadNotifications = content.filter((n) => !n.isRead);
            setUnreadCount(unreadNotifications.length);
          }
        }
        // Return the unread count to callers so they can react to changes.
        return metadata?.totalElements || 0;
      } catch (error) {
        console.error("Error searching notifications:", error);
        // message.error('Failed to load notifications')
        // Set empty data on error
        setNotifications([]);
        setMetadata({
          size: 10,
          number: 0,
          totalPages: 0,
          totalElements: 0,
        });
        return 0;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Mark notifications as read
  const markAsRead = useCallback(async (ids: number[]) => {
    try {
      await notificationApi.markAsRead(ids);

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) =>
          ids.includes(notification.id) ? { ...notification, isRead: true } : notification
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - ids.length));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      message.error("Failed to mark notifications as read");
    }
  }, []);

  // Get notification detail
  const getNotificationDetail = useCallback(
    async (id: number): Promise<NotificationDetail | null> => {
      try {
        return await notificationApi.getDetail(id);
      } catch (error) {
        console.error("Error fetching notification detail:", error);
        message.error("Failed to load notification detail");
        return null;
      }
    },
    []
  );

  return {
    notifications,
    loading,
    metadata,
    unreadCount,
    isConnected,
    searchNotifications,
    markAsRead,
    getNotificationDetail,
    connect,
    disconnect,
  };
};
